/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ImageIcon,
  SearchInput,
  SkeletonLoading,
} from '@saleshandy/design-system';
import classNames from 'classnames';
import { Table } from 'react-bootstrap';
import NoSearchResultIcon from '../../../shared/components/images/no-search-result-icon';
import MultiSegmentProgressBar from '../../../shared/components/onboarding-modal/components/multi-segment-progress-bar';
import ExportButton from '../../../shared/design-system/components/atoms/export-button';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../shared/design-system/components/overlay';
import TablePagination from '../../../shared/design-system/ui/table/components/table-pagination';
import { RequestStatus } from '../../../shared/enums/request-status';
import { getFormattedEmailDate } from '../../../shared/functions/date';
import toaster from '../../../shared/toaster';
import {
  executeOnRequestStatus,
  redirectWithoutRefresh,
} from '../../../shared/utils';
import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';
import NoSearchResult from '../../leads/components/no-search-result';
import { getProgressPopoverSections, getOverlayPlacement } from './helper';
import { PaginationAccordionTableProps } from './types';
import { NotificationActionType, NotificationType } from '../../home/types';
import { formatLargeNumberToK } from '../../home/components/header/header-helper';
import { Images } from '../../../shared/app-constants';
import { getUserTimeZone } from '../../../shared/utils/user-details';

/**
 * PaginationAccordionTableModule is a React component that displays a paginated accordion table.
 * It allows users to search through the data, view details in an accordion format, and paginate through results.
 * It includes features like expandable rows, search functionality, and pagination controls.
 * @returns React Component
 */
const PaginationAccordionTable: React.FC<PaginationAccordionTableProps> = React.memo(
  ({
    pollingNotifications,
    uploadFileRequestStatus,
    emailVerifierUploadedFilesList,
    totalEmailVerifierUploadedFilesList,
    downloadEmailVerifiedFileRequestStatus,
    paginationState,
    downloadEmailVerifiedFileRequests = () => {},
    setIsVerifiedEmailDatas = () => {},
    resetDownloadEmailVerifiedFileRequests = () => {},
    getVerifiedEmailFileDetailsByIdRequests = () => {},
    getEmailVerifierUploadedFilesListRequest,
    resetPaginationPage,
  }) => {
    const [searchPaginationState, setSearchPaginationState] = useState({
      search: '',
      searchInputValue: '',
      pagination: { page: 1, limit: 25 },
      hasSyncedFromUrl: false,
    });
    const [visibleOverlayIndex, setVisibleOverlayIndex] = useState<
      number | null
    >(null);

    const userTimezone = getUserTimeZone();

    const [overlayPlacement, setOverlayPlacement] = useState<Placement>(
      Placement.Bottom,
    );

    const handleOverlayToggle = useCallback((index: number) => {
      const el = document.querySelector(
        `.popover-anchor-${index}`,
      ) as HTMLElement;

      if (el) {
        const placement = getOverlayPlacement(el);
        setOverlayPlacement(placement);
      }

      setVisibleOverlayIndex((prev) => (prev === index ? null : index));
    }, []);

    const disabledIdList = useMemo(
      () =>
        emailVerifierUploadedFilesList
          ?.filter(
            (item) =>
              item?.status === 'pending' || item?.status === 'inProgress',
          )
          ?.map((item) => item?.id),
      [emailVerifierUploadedFilesList],
    );

    const disabledIds = useCallback(
      (id: number | string) => disabledIdList?.includes(id),
      [disabledIdList],
    );

    const handlePaginationChange = useCallback(
      ({ page: newPage, limit: newLimit }: { page: number; limit: number }) => {
        setSearchPaginationState((prev) => ({
          ...prev,
          pagination: { page: newPage, limit: newLimit },
        }));
      },
      [],
    );

    const failedIds = useMemo(
      () =>
        emailVerifierUploadedFilesList
          ?.filter((item) => item.status === 'failed')
          ?.map((item) => item?.id),
      [emailVerifierUploadedFilesList],
    );

    const isPendingOrIdle = useMemo(
      () =>
        [RequestStatus.Pending, RequestStatus.Ideal]?.includes(
          uploadFileRequestStatus?.status,
        ),
      [uploadFileRequestStatus.status],
    );

    // ** Memoized Values **
    const inProgressVerificationIds = useMemo(
      () =>
        emailVerifierUploadedFilesList
          ?.filter(
            (item) =>
              item?.status === 'inProgress' || item?.status === 'pending',
          )
          ?.map((item) => item?.id),
      [emailVerifierUploadedFilesList],
    );

    const completedEmailVerificationId = useMemo(() => {
      let verificationId = null;

      if (pollingNotifications?.notifications?.length > 0) {
        verificationId =
          pollingNotifications?.notifications?.find(
            (notification) =>
              notification?.actionType ===
                NotificationActionType.BulkOperation &&
              notification?.notificationType ===
                NotificationType.EmailVerifier &&
              notification?.completedAt,
          )?.metaData?.importFile?.id || null;
      }

      return verificationId;
    }, [JSON.stringify(pollingNotifications?.notifications)]);

    const filteredAccordionData = useMemo(
      () =>
        emailVerifierUploadedFilesList?.filter((item) =>
          item.fileName
            ?.toLowerCase()
            ?.includes(searchPaginationState.search?.toLowerCase()),
        ),
      [searchPaginationState.search, emailVerifierUploadedFilesList],
    );

    // download api call
    const handleExport = useCallback(
      (item) => {
        const payload = {
          emailVerificationId: item,
        };
        downloadEmailVerifiedFileRequests(payload);
      },
      [downloadEmailVerifiedFileRequests],
    );

    // ClassNames
    const paginationAccordionClassName = useMemo(
      () =>
        classNames('pagination-accordion-table-container', {
          'no-result-found':
            totalEmailVerifierUploadedFilesList <= 0 && !isPendingOrIdle,
        }),
      [totalEmailVerifierUploadedFilesList, isPendingOrIdle],
    );

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);

      const pageParam = Number(params.get('page') || 1);
      const limitParam = Number(params.get('limit') || 25);
      const searchParam = params.get('search') || '';

      setSearchPaginationState((prev) => ({
        ...prev,
        pagination: { page: pageParam, limit: limitParam },
        search: searchParam,
        searchInputValue: searchParam,
        hasSyncedFromUrl: true,
      }));
    }, []);

    useEffect(() => {
      if (!paginationState) return;

      setSearchPaginationState((prev) => ({
        ...prev,
        pagination: {
          page: paginationState?.page || 1,
          limit: paginationState?.limit || 25,
        },
        search: paginationState?.search || '',
        searchInputValue: '',
        hasSyncedFromUrl: true,
      }));
      resetPaginationPage();
    }, [paginationState]);

    // email verified list api call
    useEffect(() => {
      if (!searchPaginationState.hasSyncedFromUrl) return;

      const { page, limit } = searchPaginationState.pagination;
      const search = encodeURIComponent(searchPaginationState?.search || '');

      redirectWithoutRefresh(
        `/email-verifier?page=${page}&limit=${limit}${
          search ? `&search=${search}` : ''
        }`,
      );

      getEmailVerifierUploadedFilesListRequest({
        page: searchPaginationState.pagination.page,
        limit: searchPaginationState.pagination.limit,
        search: searchPaginationState.search || '',
      });

      // it is for setting the search state in redux
      setIsVerifiedEmailDatas(searchPaginationState.search);
    }, [
      searchPaginationState.hasSyncedFromUrl,
      searchPaginationState.search,
      searchPaginationState.pagination,
    ]);

    // download email file api status based condition
    useEffect(() => {
      executeOnRequestStatus({
        status: downloadEmailVerifiedFileRequestStatus,
        onSuccess: () => {
          toaster.success(
            'Your CSV file send to your registered email address.',
          );
          resetDownloadEmailVerifiedFileRequests();
        },
        onFailed: () => {
          toaster.error('Failed to download file');
          resetDownloadEmailVerifiedFileRequests();
        },
      });
    });

    useEffect(() => {
      if (
        completedEmailVerificationId &&
        inProgressVerificationIds?.length > 0 &&
        inProgressVerificationIds?.includes(completedEmailVerificationId)
      ) {
        getVerifiedEmailFileDetailsByIdRequests(completedEmailVerificationId);
      }
    }, [completedEmailVerificationId, inProgressVerificationIds]);

    return (
      <div className={paginationAccordionClassName}>
        <div className="pagination-table-header">
          {isPendingOrIdle ? (
            <SkeletonLoading width={121} height={14} />
          ) : (
            <span>Results</span>
          )}

          {isPendingOrIdle ? (
            <div className="skeleton-show">
              <SkeletonLoading width={18} height={18} />
              <SkeletonLoading width={65} height={9} />
            </div>
          ) : (
            <SearchInput
              search={searchPaginationState.searchInputValue}
              placeholder="Search"
              setSearch={(value) => {
                setSearchPaginationState((prev) => ({
                  ...prev,
                  searchInputValue: value,
                }));
              }}
              onClearSearch={() => {
                setSearchPaginationState((prev) => ({
                  ...prev,
                  searchInputValue: '',
                  search: '',
                  page: 1,
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchPaginationState((prev) => ({
                    ...prev,
                    search: prev.searchInputValue,
                    pagination: { ...prev.pagination, page: 1 },
                  }));
                }
              }}
              onSearch={() => {}}
              className="email-search-input"
            />
          )}
        </div>
        {isPendingOrIdle || filteredAccordionData?.length > 0 ? (
          <>
            <Table className="pagination-table-contaier">
              <thead>
                {isPendingOrIdle ? (
                  <tr>
                    <th>
                      <SkeletonLoading width={121} height={14} />
                    </th>
                    <th>
                      <SkeletonLoading width={77} height={14} />
                    </th>
                    <th>
                      <SkeletonLoading width={77} height={14} />
                    </th>
                    <th>
                      <SkeletonLoading width={37} height={14} />
                    </th>
                    <th>
                      <SkeletonLoading width={45} height={14} />
                    </th>
                  </tr>
                ) : (
                  <tr>
                    <th>File Name</th>
                    <th>Emails</th>
                    <th>Results</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {isPendingOrIdle
                  ? Array.from({
                      length: searchPaginationState.pagination.limit,
                    })?.map((_, index) => (
                      <tr key={`skeleton-row-${index + 1}`}>
                        <td>
                          <SkeletonLoading width="100%" height={10} />
                        </td>
                        <td>
                          <SkeletonLoading width="100%" height={10} />
                        </td>
                        <td>
                          <SkeletonLoading width="100%" height={10} />
                        </td>
                        <td>
                          <SkeletonLoading width="100%" height={10} />
                        </td>
                        <td>
                          <SkeletonLoading width="100%" height={18} />
                        </td>
                      </tr>
                    ))
                  : emailVerifierUploadedFilesList?.map((item, index) => (
                      <tr>
                        {/* FileName */}
                        <td>
                          {item?.fileName ? (
                            <span className="pagination-table-filename">
                              <OverlayTooltip
                                text={item?.fileName}
                                className="pagination-accordion-tooltips"
                              >
                                <span>
                                  {item?.fileName?.replace(/\.[^/.]+$/, '')}
                                </span>
                              </OverlayTooltip>
                            </span>
                          ) : (
                            <SkeletonLoading width={314} height={10} />
                          )}
                        </td>

                        {/* 100 Valid */}
                        <td>
                          {disabledIds(item?.id) ? (
                            <SkeletonLoading
                              width="100%"
                              height={10}
                              className="disabled"
                            />
                          ) : failedIds?.includes(item?.id) ? (
                            <span className="accordion-empty">-</span>
                          ) : (
                            <div className="email-percentage-conatiner">
                              <span className="email-valid-percentage">
                                <span>
                                  {item?.statusContactsCount?.validContacts
                                    ?.percentage || 0}
                                  %
                                </span>
                                Valid
                              </span>
                              <div className="table-email-stats-count">
                                <span className="email-stats-count">
                                  {item?.totalContacts !== 0 &&
                                    `${formatLargeNumberToK(
                                      item?.totalContacts,
                                    )} Total Emails`}
                                </span>
                              </div>
                            </div>
                          )}
                        </td>

                        {/* ProgressBar */}
                        <td>
                          {disabledIds(item?.id) ? (
                            <SkeletonLoading
                              width="100%"
                              height={10}
                              className="disabled"
                            />
                          ) : failedIds?.includes(item?.id) ? (
                            <OverlayTooltip
                              text="We couldn't complete your upload. Try again later."
                              className="pagination-accordion-tooltips"
                            >
                              <div className="accordion-failed">
                                <ImageIcon
                                  src={Images.AlertTriangleRed}
                                  alt="alert-triangle"
                                />
                                <span>Failed</span>
                              </div>
                            </OverlayTooltip>
                          ) : (
                            <OverlayPopover
                              show={visibleOverlayIndex === index}
                              onToggle={() => handleOverlayToggle(index)}
                              key={`overlay-${index + 1}`}
                              className="progress-overlay-popover"
                              content={
                                <>
                                  <div className="progress-popover-container">
                                    {getProgressPopoverSections(item)?.map(
                                      (section) => (
                                        <div
                                          className="progress-popover"
                                          key={section?.label}
                                        >
                                          <div className="progress-popover-child-container">
                                            <div className="progress-main-content">
                                              <div
                                                className={section?.iconClass}
                                              />
                                              <span>{section?.label}</span>
                                              <span className="progress-percentage">
                                                {section?.count !== 0 &&
                                                  section?.count}{' '}
                                                <span>
                                                  {section?.percentage || 0}%
                                                </span>
                                              </span>
                                            </div>

                                            {section?.children && (
                                              <div className="progress-popover-child">
                                                {section?.children?.map(
                                                  (child) => (
                                                    <div
                                                      className="progress-main-content"
                                                      key={child?.label}
                                                    >
                                                      <span className="progress-popover-label">
                                                        {child?.label}
                                                      </span>
                                                      <span className="progress-percentage-child">
                                                        {child?.count !== 0 &&
                                                          child?.count}{' '}
                                                        <span>
                                                          {child?.percentage ||
                                                            0}
                                                          %
                                                        </span>
                                                      </span>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </>
                              }
                              trigger={TriggerType.Hover}
                              placement={overlayPlacement}
                            >
                              <div
                                className={`accordion-progress-bar popover-anchor-${index}`}
                                {...accessibleOnClick(() =>
                                  handleOverlayToggle(index),
                                )}
                                onMouseLeave={() =>
                                  setVisibleOverlayIndex(null)
                                }
                              >
                                <MultiSegmentProgressBar
                                  segments={[
                                    {
                                      percentage:
                                        item?.statusContactsCount?.validContacts
                                          ?.percentage || 0,
                                      color: '#10b981',
                                      label: 'Valid',
                                    }, // green
                                    {
                                      percentage:
                                        item?.statusContactsCount
                                          ?.invalidContacts?.percentage || 0,
                                      color: '#f87171',
                                      label: 'Invalid',
                                    }, // red
                                    {
                                      percentage:
                                        item?.statusContactsCount?.riskyContacts
                                          ?.percentage || 0,
                                      color: '#fbbf24',
                                      label: 'Risky',
                                    }, // yellow
                                  ]}
                                />
                              </div>
                            </OverlayPopover>
                          )}
                        </td>

                        {/* Date */}
                        <td>
                          <span className="email-stats-date">
                            {getFormattedEmailDate(
                              item?.createdAt,
                              userTimezone,
                              true,
                            )}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="table-action-btn">
                          <div
                            {...accessibleOnClick((e) => {
                              e.stopPropagation();
                            })}
                            className={`pagination-accordion-export-button ${
                              disabledIds(item?.id) ? 'disabled' : ''
                            }`}
                          >
                            <ExportButton
                              handleExport={() => handleExport(item?.id)}
                              customTooltupText="Download"
                              iconOnly
                              disableExportBtn={disabledIds(item?.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </Table>
            {!isPendingOrIdle && totalEmailVerifierUploadedFilesList > 25 && (
              <TablePagination
                page={searchPaginationState.pagination.page}
                limit={
                  searchPaginationState?.pagination?.limit ||
                  totalEmailVerifierUploadedFilesList
                }
                totalRows={totalEmailVerifierUploadedFilesList}
                customOptions={[25, 50, 100]}
                onPaginationOptionsChange={handlePaginationChange}
              />
            )}
          </>
        ) : (
          <NoSearchResult
            description={
              <div>
                Results matching this query could not be displayed.
                <br />
                Please try refining your search.
              </div>
            }
            title="No results found"
          >
            <NoSearchResultIcon />
          </NoSearchResult>
        )}
      </div>
    );
  },
);

export default PaginationAccordionTable;
